import * as React from "react";
import { GatsbyImage } from "gatsby-plugin-image";
import { IGatsbyImageData } from "gatsby-plugin-image/dist/src/components/gatsby-image.browser";
import { graphql } from "gatsby";

import { Lightbox, Slide, GatsbyImageSlide } from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import classNames from "classnames";

function isGatsbyImageSlide(slide: Slide): slide is GatsbyImageSlide {
  return slide.type === "gatsby-image-slide";
}

export const query = graphql`
  fragment GalleryImages on File {
    thumb: childImageSharp {
      gatsbyImageData(
        width: 256
        aspectRatio: 1
        quality: 60
        placeholder: BLURRED
        layout: CONSTRAINED
        transformOptions: { fit: COVER, cropFocus: ATTENTION }
      )
    }
    large: childImageSharp {
      gatsbyImageData(
        quality: 70
        placeholder: BLURRED
        layout: CONSTRAINED
        transformOptions: { fit: CONTAIN, cropFocus: ATTENTION }
      )
    }
  }
`;

export type GalleryItemProps = {
  thumb: IGatsbyImageData;
  large: IGatsbyImageData;
};

export function Gallery({
  items,
  cols = 4,
}: {
  items: GalleryItemProps[];
  cols?: number;
}) {
  const [openLightbox, setOpenLightbox] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<number | null>(null);
  return (
    <div
      className={classNames("grid gap-8", {
        "lg:grid-cols-1": cols === 1,
        "lg:grid-cols-2": cols === 2,
        "lg:grid-cols-3": cols === 3,
        "lg:grid-cols-4": cols === 4,
        "lg:grid-cols-5": cols === 5,
      })}
    >
      {items.map((item, index) => (
        <GalleryItem
          key={index}
          item={item}
          onSelect={() => {
            setSelectedItem(index);
            setOpenLightbox(true);
          }}
          index={index}
        />
      ))}
      <Lightbox
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        slides={items.map((item) => ({
          type: "gatsby-image-slide",
          gatsbyImageData: item.large,
        }))}
        index={selectedItem ?? 0}
        render={{
          slide: ({ slide }) => {
            if (isGatsbyImageSlide(slide)) {
              return (
                <GatsbyImage
                  alt={slide.alt ?? ""}
                  image={slide.gatsbyImageData}
                  loading={"lazy"}
                />
              );
            }
            return undefined;
          },
        }}
      />
    </div>
  );
}

export function GalleryItem({
  item,
  onSelect,
}: {
  item: GalleryItemProps;
  index: number;
  onSelect: (image: IGatsbyImageData) => void;
}) {
  return (
    <button onClick={() => onSelect(item.large)} className={"aspect-square"}>
      <GatsbyImage
        alt={""}
        image={item.thumb}
        loading={"lazy"}
        className={"w-full h-full"}
      />
    </button>
  );
}
